import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Switch,
  Box,
  Text,
  Tooltip,
  Icon,
  keyframes,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  MdLocationOn,
  MdMyLocation,
  MdOutlineExplore,
  MdEvStation,
  MdBolt,
} from "react-icons/md";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from 'mapbox-gl';

// 1. Force Webpack to process the isolated CSP worker
// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';

// 2. Attach the worker globally to your mapbox instance
import useAxios from "axios-hooks";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Card from "components/card/Card";
import SearchLocation from "../components/SearchLocation";
import { loggedInUserState } from "atoms/loggedInUserState";
import { toastState } from "atoms/toastState";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";
import { onlyUAdmins } from "utils/accessLevels";

// Glass morphism styled components
mapboxgl.workerClass = MapboxWorker;
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const GlassMorphismCard = styled(Card)`
background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(35, 169, 121, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;
  
  &:hover {
    border-color: rgba(35, 169, 121, 0.4);
    box-shadow: 0 8px 32px rgba(35, 169, 121, 0.15);
  }
`;

const MapContainer = styled(Box)`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(35, 169, 121, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: 0 8px 32px rgba(35, 169, 121, 0.12);
  }
`;

const StyledFormControl = styled(FormControl)`
  margin-bottom: 16px;
  transition: all 0.3s ease;

  & > label {
    color: #333;
    margin-bottom: 6px;
    font-weight: 500;
  }

  & input {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(35, 169, 121, 0.2);
    border-radius: 8px;
    max-width: 200px;
    transition: all 0.2s ease;

    &:focus {
      border-color: #23A979;
      box-shadow: 0 0 0 1px #23A979;
    }

    &:hover:not(:focus) {
      border-color: rgba(35, 169, 121, 0.5);
    }
  }
`;

const FuturisticButton = styled(Button)`
  background: #23A979;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(35, 169, 121, 0.3);
  color: white;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  padding: 0 24px;
  height: 40px;

  &:hover {
    background: #1C8D63;
    box-shadow: 0 6px 16px rgba(35, 169, 121, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(35, 169, 121, 0.3);
  }
`;

const CoordinateIcon = styled(Icon)`
  color: #23A979;
  margin-right: 8px;
`;

const ChargerMarker = styled(Box)`
  /* Simple and direct positioning */
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -100%);

  /* Container for marker elements */
  .marker-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 36px;
  }

  /* Circle background for icon */
  .marker-bg {
    width: 24px;
    height: 24px;
    background: #23A979;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
      0 0 0 2px white,
      0 0 8px rgba(35, 169, 121, 0.8);
    animation: ${pulse} 2s infinite ease-in-out;
    z-index: 2;
  }

  /* Icon styling */
  .marker-icon {
    color: white;
    font-size: 14px;
  }

  /* Triangle pointer at bottom */
  .marker-pointer {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 8px;
    background: #23A979;
    clip-path: polygon(0 0, 100% 0, 50% 100%);
    z-index: 1;
  }
`;

const token =
  "pk.eyJ1IjoiaGFyZXNocCIsImEiOiJjbGFlNjViMjUwNnJyM3dvNWp2dDg5aWh3In0.96qsX4fpBmX7yB9FRVejhQ";

const formFields = [
  {
    label: "Latitude",
    placeholder: "Latitude",
    type: "input",
    name: "Latitude",
  },
  {
    label: "Longitude",
    placeholder: "Longitude",
    type: "input",
    name: "Longitude",
  },
];

const Location = (props) => {
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const setToast = useSetRecoilState(toastState);
  const { id, getStation, data } = props;
  console.log("data", data);
  const [coordinates, setCoordiantes] = useState({
    Latitude: 17.723491627909628,
    Longitude: 83.30958857803418,
  });

  const [viewport, setViewport] = useState({
    height: "100%",
    width: "100%",
    longitude: 83.30958857803418,
    latitude: 17.723491627909628,
    zoom: 15,
  });

  //axios hook
  const [{ data: updatedData, loading, error }, execute] = useAxios(
    {
      method: "PATCH",
      url: `/api/locations/update/${id}`,
    },
    { manual: true },
  );
  //update schema
  const updateSchema = yup.object().shape({
    Latitude: yup
      .number()
      .min(-90, "Latitude must be greater than or equal to -90")
      .max(90, "Latitude must be less than or equal to 90")
      .required("Latitude is required"),
    Longitude: yup
      .number()
      .min(-180, "Longitude must be greater than or equal to -180")
      .max(180, "Longitude must be less than or equal to 180")
      .required("Longitude is required"),
  });

  // form  validation
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(updateSchema),
  });

  const updateHandler = async () => {
    const formValues = getValues();
    let updateData = {};
    if (
      formValues.Latitude !== data?.Latitude ||
      formValues.Longitude !== data?.Longitude
    ) {
      updateData["Coordinates"] = {
        Y: parseFloat(formValues.Latitude),
        X: parseFloat(formValues.Longitude),
      };
    }

    if (Object.keys(updateData).length > 0) {
      try {
        const response = await execute({ data: updateData });
        setToast({
          isOpen: true,
          title: "Location Updated successfully",
          status: "success",
        });
        getStation();
      } catch (error) {
        const err = error?.response?.data?.error;
        setToast({
          isOpen: true,
          title: "Error occured when updating location",
          description: err ? err : null,
          status: "error",
        });
      }
    } else {
      setToast({
        isOpen: true,
        title: "Nothing new to update",
        status: "warning",
        position: "top",
      });
    }
  };

  const clickHandler = (event) => {
    const { lng, lat } = event.lngLat;
    setCoordiantes((prev) => {
      return { ...prev, Longitude: lng, Latitude: lat };
    });
    setViewport((prev) => {
      return { ...prev, longitude: lng, latitude: lat };
    });
    setValue("Latitude", lat);
    setValue("Longitude", lng);
  };

  const moveHandler = (evt) => {
    setViewport(evt.viewState);
  };

  const inputChangeHandler = (e) => {
    const { name, value } = e.target;
    console.log("value type", typeof value);
    if (
      !isNaN(value) &&
      ((name === "Latitude" && value <= 90 && value >= -90) ||
        (name === "Longitude" && value <= 180 && value >= -180))
    ) {
      setCoordiantes((prev) => {
        //chnage the value to float
        return { ...prev, [name]: parseFloat(value) };
      });
      let LName = name.toLowerCase();
      setViewport((prev) => {
        return { ...prev, [LName]: parseFloat(value) };
      });
    }
  };

  const searchResultsHandler = (d) => {
    setViewport((prev) => {
      return { ...prev, latitude: d.Latitude, longitude: d.Longitude };
    });
    setCoordiantes({ Latitude: d.Latitude, Longitude: d.Longitude });
    setValue("Latitude", d.Latitude);
    setValue("Longitude", d.Longitude);
  };

  useEffect(() => {
    if (data?.Coordinates?.x && data?.Coordinates?.y) {
      setViewport((prev) => {
        return {
          ...prev,
          latitude: data.Coordinates.y,
          longitude: data.Coordinates.x,
        };
      });
      setCoordiantes({
        Latitude: data.Coordinates.y,
        Longitude: data.Coordinates.x,
      });
      setValue("Latitude", data.Coordinates.y);
      setValue("Longitude", data.Coordinates.x);
    }
  }, [data]);

  return (
    <GlassMorphismCard>
      <Flex mb={{ base: 3, md: 4 }} alignItems="center">
        <Icon
          as={MdEvStation}
          boxSize={{ base: 5, md: 6 }}
          color="brand.500"
          mr={2}
        />
        <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="600">
          Location Coordinates
        </Text>
      </Flex>

      <Grid
        templateAreas={{
          base: `"search" "map" "form"`,
          md: `"map search" "form search"`,
        }}
        gap={{ base: 3, md: 5 }}
        gridTemplateColumns={{ base: "1fr", md: "60% 40%" }}
      >
        <GridItem area={"search"}>
          <Box
            p={{ base: 3, md: 4 }}
            bg="rgba(255, 255, 255, 0.4)"
            borderRadius="12px"
            border="1px solid rgba(35, 169, 121, 0.15)"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
            transition="all 0.3s ease"
            _hover={{
              borderColor: "rgba(35, 169, 121, 0.3)",
              boxShadow: "0 6px 24px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Text
              mb={{ base: 2, md: 3 }}
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="500"
              color="#333"
              display="flex"
              alignItems="center"
            >
              <Icon
                as={MdOutlineExplore}
                color="brand.500"
                mr={2}
                boxSize={{ base: 4, md: 5 }}
              />{" "}
              Search Location
            </Text>
            <SearchLocation
              setLocation={(item) => searchResultsHandler(item)}
            />
          </Box>
        </GridItem>

        <GridItem area={"map"}>
          <Box position="relative">
            <MapContainer height={{ base: "300px", md: "400px" }} width="100%">
              <Map
                {...viewport}
                mapLib={mapboxgl}
                mapboxAccessToken={token}
                onMove={moveHandler}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                onClick={(e) => clickHandler(e)}
              >
                <Marker
                  latitude={coordinates.Latitude}
                  longitude={coordinates.Longitude}
                >
                  <ChargerMarker>
                    <div className="marker-container">
                      <div className="marker-bg">
                        <Icon as={MdEvStation} className="marker-icon" />
                      </div>
                      <div className="marker-pointer" />
                    </div>
                  </ChargerMarker>
                </Marker>
              </Map>
            </MapContainer>
            <Box
              position="absolute"
              top={{ base: "8px", md: "10px" }}
              right={{ base: "8px", md: "10px" }}
              bg="rgba(255, 255, 255, 0.8)"
              borderRadius="8px"
              p={{ base: 1.5, md: 2 }}
              boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
              fontSize={{ base: "xs", md: "sm" }}
            >
              <Text
                fontWeight="500"
                mb={1}
                display="flex"
                alignItems="center"
                fontSize={{ base: "xs", md: "sm" }}
              >
                <Icon
                  as={MdEvStation}
                  color="brand.500"
                  mr={1}
                  boxSize={{ base: 3, md: 4 }}
                />
                Charger Position
              </Text>
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.600">
                Lat: {coordinates?.Latitude?.toFixed(6)}
              </Text>
              <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.600">
                Lng: {coordinates?.Longitude?.toFixed(6)}
              </Text>
            </Box>
          </Box>
        </GridItem>

        <GridItem area={"form"}>
          <Box
            p={{ base: 3, md: 4 }}
            bg="rgba(255, 255, 255, 0.4)"
            borderRadius="12px"
            border="1px solid rgba(35, 169, 121, 0.15)"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
            transition="all 0.3s ease"
          >
            <Text
              mb={{ base: 3, md: 4 }}
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="500"
              color="#333"
            >
              Coordinate Details
            </Text>

            <form>
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={{ base: 3, md: 4 }}
              >
                {formFields.map((item, index) => (
                  <Controller
                    key={index}
                    name={item.name}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <StyledFormControl
                        isRequired
                        isInvalid={errors[item.name]}
                      >
                        <FormLabel
                          fontSize="sm"
                          display="flex"
                          alignItems="center"
                        >
                          <CoordinateIcon
                            as={item.name === "Latitude" ? MdEvStation : MdBolt}
                            boxSize={4}
                          />
                          {item.label}
                        </FormLabel>
                        <Input
                          {...field}
                          fontSize="sm"
                          placeholder={item.placeholder}
                          type={item.type}
                          isRequired
                          onChange={(event) => {
                            field.onChange(event);
                            inputChangeHandler(event);
                          }}
                        />
                        <FormErrorMessage>
                          {errors[item.name] && errors[item.name].message}
                        </FormErrorMessage>
                      </StyledFormControl>
                    )}
                  />
                ))}
              </Grid>

              <RoleBasedRender roles={onlyUAdmins}></RoleBasedRender>
              <RoleBasedRender roles={onlyAdmins}>
                <Flex mt={6} justify="center">
                  <Tooltip
                    label="Update charging station location"
                    hasArrow
                    placement="top"
                  >
                    <FuturisticButton
                      onClick={handleSubmit(updateHandler)}
                      isLoading={loading}
                      leftIcon={<Icon as={MdEvStation} />}
                    >
                      Save Charger Location
                    </FuturisticButton>
                  </Tooltip>
                </Flex>
              </RoleBasedRender>
            </form>
          </Box>
        </GridItem>
      </Grid>
    </GlassMorphismCard>
  );
};

export default Location;
